import React from "react";

import { GradientDefs } from "../../GradientDefs";

/**
 * @component PersonCircleIcon
 * @description
 *
 * The React Icon component is a visual element that displays an icon to represent a concept, object, or action.
 * The Icon component is
 * customizable, allowing for variations in size, color, and style to fit the needs of the application.
 *
 * @prop {string} [gradient] - Optional CSS linear-gradient string to apply a custom fill.
 * Format: "linear-gradient(<angle>deg, <color1>, <color2>, ...)"
 *
 * @example
 * return (
 * <PersonCircleIcon gradient="linear-gradient(135deg, #4da2ed, #f72222)" />
 * )
 *
 * @example
 * return (
 *   <PersonCircleIcon className="mr-2" />
 * )
 *
 * @see
 * https://system.activecollab.com/?path=/story/foundation-icons-icons--icons
 * @see
 * https://design.activecollab.com/docs/foundations/icons
 */
const PersonCircleIcon = React.forwardRef(
  (
    {
      gradient,
      ...props
    }: React.SVGProps<SVGSVGElement> & {
      gradient?: string;
    },
    ref?: React.Ref<SVGSVGElement>
  ) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={24}
      height={24}
      viewBox="0 0 24 24"
      data-testid="PersonCircleIcon"
      fill={
        gradient
          ? "url(#icon-gradient)"
          : props?.fill
          ? props.fill
          : "var(--color-theme-600)"
      }
      focusable={false}
      ref={ref}
      {...props}
    >
      <GradientDefs gradient={gradient} />
      <path
        fillRule="evenodd"
        d="m12 2 .28.004.316.013.244.018.402.041.14.019.267.04.162.029.19.036a10.01 10.01 0 0 1 7.864 8.151L21.8 10l.046.244.056.352.031.244.017.154.022.257.02.367q.009.19.008.382l-.002.207-.01.299-.022.323-.04.397-.032.238-.059.354-.062.308-.05.22-.046.184a10 10 0 0 1-.12.422l-.092.283-.13.357-.113.283-.094.215-.161.341-.136.265-.178.32-.13.217q-.218.356-.47.696l-.274.356-.229.273-.14.157-.258.274-.226.224-.104.099-.238.215-.312.262-.398.305-.284.199-.33.213a10 10 0 0 1-.35.208l-.266.145-.383.19-.291.132-.346.142-.358.132-.31.1-.402.116-.138.035-.187.045-.347.072-.443.074-.305.04-.16.016-.423.032-.271.011L12 22l-.2-.002-.331-.012-.373-.026-.167-.017-.164-.019-.246-.033-.336-.056-.3-.06-.31-.072-.415-.113-.297-.093-.323-.112-.284-.11-.343-.147-.266-.124-.28-.14-.159-.086-.328-.188-.177-.108-.297-.193-.383-.272-.316-.247-.316-.267-.333-.307-.208-.207-.247-.262-.17-.192-.245-.294-.18-.233a10 10 0 0 1-.361-.511l-.171-.27-.063-.103-.1-.17-.142-.259-.198-.392-.1-.213-.126-.295-.11-.281a10 10 0 0 1-.312-.984l-.096-.403-.062-.309-.059-.354-.06-.49-.028-.376-.013-.278A10 10 0 0 1 2 12C2 6.477 6.477 2 12 2m0 16q-1.735 0-3.476.357a2 2 0 0 0-.831.385q.396.255.822.46l.287.133.295.122.356.13.292.09.146.042.27.068.248.055.281.051q.162.027.325.047l.38.037.28.017L12 20l.368-.008.167-.01.167-.012q.18-.016.36-.04l.289-.044.24-.044.248-.054.313-.08.16-.047.234-.075.3-.107.395-.163.247-.114.304-.156.214-.12a8 8 0 0 0 .3-.183 2 2 0 0 0-.83-.386A17.3 17.3 0 0 0 12 18m0-14a8 8 0 0 0-8 8l.008.352.014.24.025.28.024.196.04.265.043.236.063.29.046.184.083.29.034.11.113.329.17.42.07.158q.135.29.29.567l.202.34.126.194.241.34.18.23.134.162.235.264a4 4 0 0 1 1.981-1.05A19.3 19.3 0 0 1 12 16q1.938 0 3.877.398c.76.156 1.444.525 1.983 1.048l.236-.265.193-.236.125-.163.18-.25.17-.258.097-.157.094-.16.14-.257.131-.263.103-.225.133-.321.119-.328.098-.313.104-.39.063-.291.053-.297q.03-.18.05-.365l.03-.32.01-.181Q20 12.204 20 12l-.012-.443a8 8 0 0 0-.078-.762l-.081-.449a8.01 8.01 0 0 0-5.843-6.097l-.422-.096-.353-.062-.358-.046a8 8 0 0 0-.409-.033l.359.028-.213-.019-.214-.012zm0 3a4 4 0 1 1 0 8 4 4 0 0 1 0-8m0 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4"
      />
    </svg>
  )
);
PersonCircleIcon.displayName = "PersonCircleIcon";
export default PersonCircleIcon;
